import { Word } from '@attain-apps/string-builder'
import type * as marked from 'marked'

/**
 * Used to find HTML elements produced by {@link Extension}
 */
const FINDING_CLASS = 'atn-ext'

/**
 * Since ID attributes are stripped during sanitization, we use our own data
 * attribute to store the ID
 */
const ID_CLASS_PREFIX = 'atn-id-'

/**
 * Represents a
 * {@link https://marked.js.org/using_pro#extensions Marked extension} in a
 * standardized structure for attain extensions
 *
 * @see {@link Extension.register}
 * @example
 * // This would be used to capture something like:
 * // <atn-foo prop1="a" prop2="b">this is the body</atn-foo>
 * Extension.register('foo', ({ body, properties }) => {
 *   let html = `<div>`
 *   html += '<h4>Props</h4>'
 *   html += '<ul>'
 *   for (const k in properties) {
 *     html += '<p>'
 *     html += `<em>${k}</em>`
 *     html += properties[k]
 *     html += '</p>'
 *   }
 *   html += '</ul>'
 *   html += '<h4>Body</h4>'
 *   html += `<p>${body}</p>`
 *   html += '</div>'
 *   return html
 * })
 */
export class Extension<TPropertyName extends string = string>
  implements marked.TokenizerExtension, marked.RendererExtension
{
  /**
   * Singleton instance of {@link Extension} for `inline` level elements
   */
  public static readonly INLINE_INSTANCE = new Extension('inline')
  /**
   * All instances of {@link Extension}
   *
   * __NOTE:__ inline only. Marked tokenizes blocks first, so a block-level
   * instance would claim mid-sentence tags too and split the paragraph
   * around them.
   */
  public static readonly ALL_INSTANCES = [this.INLINE_INSTANCE]

  /**
   * Lookup of {@link Extension.Renderer} by type
   */
  private static readonly _rendererByType: Partial<
    Record<string, Extension.Renderer>
  > = {}

  /**
   * Registers a custom type with its own {@link Extension.Renderer}
   *
   * __NOTE:__ multiple calls using the same type will override each other
   */
  public static register(type: string, renderer: Extension.Renderer): void {
    this._rendererByType[type] = renderer
  }

  /** @see {@link marked.TokenizerExtension.name} */
  public get name(): string {
    return `attain-apps-${this.level}`
  }
  /** @see {@link marked.TokenizerExtension.childTokens} */
  public readonly childTokens?: string[] | undefined

  /**
   * Lookup of generated HTML element IDs to corresponding
   * {@link Extension.Initializer}
   */
  private readonly _initializers: Partial<
    Record<string, Extension.Initializer>
  > = {}

  /**
   * Used by {@link tokenizer}
   */
  private readonly _regexp: RegExp =
    /^<atn-(?<type>[\w-]+)(?<props>(\s+[\w-]+="[^"]*")*)\s*>(?<body>(.|\n)*?)<\/atn-\1>/

  constructor(
    /** @see {@link marked.TokenizerExtension.level} */
    public readonly level: marked.TokenizerExtension['level'],
  ) {}

  /** @see {@link marked.TokenizerStartFunction} */
  public readonly start: marked.TokenizerStartFunction = src =>
    src.indexOf(`<atn`)

  /** @see {@link marked.TokenizerExtensionFunction} */
  public readonly tokenizer: marked.TokenizerExtensionFunction = src => {
    // Start by parsing the string
    const { 0: raw, groups: { type, props, body } = {} } =
      this._regexp.exec(src) ?? {}
    if (raw === undefined) {
      // If we did not match the string, we return undefined
      return undefined
    }
    // Next we parse the properties
    const properties: Extension.Token.Payload['properties'] = {}
    for (const { groups: { key, val } = {} } of props.matchAll(
      /\s+(?<key>[\w-]+)="(?<val>[^"]*)"/g,
    )) {
      if (key !== undefined && val !== undefined) {
        properties[key] = val
      }
    }

    // Finally we put together the complete token
    return {
      payload: {
        body,
        properties,
        type,
      },
      raw,
      type: this.name,
    } satisfies Extension.Token
  }

  /** @see {@link marked.RendererExtensionFunction} */
  public readonly renderer: marked.RendererExtensionFunction = token => {
    type Token = Partial.Semi<Extension.Token, 'payload'>
    const { payload } = token satisfies Token as Token
    if (!payload) {
      // This should never happen since we ensure there is a payload in the
      // tokenizer above, but if it does, there is nothing else we can do here
      return undefined
    }
    const renderer = Extension._rendererByType[payload.type]
    if (!renderer) {
      // If we don't have a renderer for this type, we just ignore it
      return undefined
    }
    // If we have a renderer and a payload, we put them together to get the
    // properties of the desired element
    const { innerHtml, init, styles } = renderer(payload)
    // We generate an ID for this element
    const id = Word.generate()
    // We create a callback to initialize the styles of this element, if
    // necessary
    const initStyle: Extension.Initializer | undefined = styles
      ? element => {
          element.insertAdjacentHTML(
            'afterbegin',
            `<style>.${FINDING_CLASS}.${ID_CLASS_PREFIX}${id}{${styles}}</style>`,
          )
        }
      : undefined
    // We register the initializer for this element (including styles), if
    // necessary
    this._initializers[id] =
      init && initStyle
        ? element => {
            initStyle(element)
            init(element)
          }
        : (init ?? initStyle)
    // Finally, we return the rendered HTML
    return `<span class="${FINDING_CLASS} ${ID_CLASS_PREFIX}${id}">${innerHtml}</span>`
  }

  /**
   * Initializes all HTML elements created by this {@link Extension}
   */
  public init(rootElement: HTMLElement): void {
    rootElement.querySelectorAll(`span.${FINDING_CLASS}`).forEach(element => {
      // First we extract the ID of the element (from the class list)
      const id = Array.from(element.classList)
        .find(_class => _class.startsWith(ID_CLASS_PREFIX))
        ?.substring(ID_CLASS_PREFIX.length)
      // Then we find the initializer
      const init = id && this._initializers[id]
      if (init) {
        // If we found an initializer, we use it
        init(element)
      }
    })
  }
}
export namespace Extension {
  /**
   * Represents an instance in Markdown content that is governed by our
   * {@link Extension}
   */
  export interface Token extends marked.Tokens.Generic {
    payload: Token.Payload
  }
  export namespace Token {
    export interface Payload {
      type: string
      body: string
      properties: Partial<Record<string, string>>
    }
  }

  /**
   * Renders a {@link Token.Payload} into HTML
   */
  export type Renderer = (payload: Token.Payload) => {
    /**
     * What is actually rendered for this {@link Token.Payload}
     */
    readonly innerHtml: string
    /**
     * These properties are applied to the {@link HTMLElement} created for this
     * {@link Token.Payload}
     *
     * You can also apply styles to child elements from the {@link innerHtml} by simply using the appropriate selectors
     *
     * @example
     * `
     * color: black;
     * font-size: 15px;
     *
     * .my-caption {
     *   color: gray;
     *   font-style: italic;
     * }
     *
     * .my-icon {
     *   width: 40px;
     *   height: 40px;
     * }
     * `
     */
    readonly styles?: string
    /**
     * Since JavaScript is not allowed in the {@link html} (due to sanitization that
     * prevents XSS), you can use this to add JavaScript logic
     */
    readonly init?: Initializer
  }

  /**
   * Used to initalize the HTML produced by {@link Renderer}
   */
  export type Initializer = (element: Element) => void
}
